/**
 * Generic refcounted subscriber registry for server-pushed rooms.
 *
 * Captures the live-domain idiom in one place: keyed callback sets, a room
 * join when a key's set first becomes non-empty (or on every subscribe for
 * rooms whose server-side join is idempotent and historically emitted per
 * subscriber), a room leave when the last subscriber for a key departs, and
 * replay of every active key's join after a reconnect.
 *
 * Unkeyed (singleton-room) domains use a single fixed key — `null` — so Set
 * and Map shaped registries share this one implementation.
 *
 * Stale unsubscribe closures (run after clear() reset the registry on
 * disconnect) are inert: they neither emit a leave nor disturb a re-created
 * subscriber set for the same key.
 */

export interface SubscriptionRegistryOptions<K> {
  /** Emit the server-side room join for a key. */
  join: (key: K) => void
  /**
   * Emit the server-side room leave for a key. Omit for shared rooms that
   * must never be left implicitly (e.g. global_events, shared between the
   * profile-change and activity-feed subscriptions).
   */
  leave?: (key: K) => void
  /**
   * Emit the join on every subscribe call rather than only on the empty →
   * non-empty transition. Session and global-token rooms join per
   * subscriber; the server-side join is idempotent.
   */
  joinOnEverySubscribe?: boolean
}

export class SubscriptionRegistry<K, Data> {
  private readonly callbacks = new Map<K, Set<(data: Data) => void>>()

  constructor(private readonly options: SubscriptionRegistryOptions<K>) {}

  /**
   * Register a callback under a key, emitting the room join per the
   * configured policy. Returns the unsubscribe function: it removes the
   * callback and, when the key's set empties, deletes the key and emits the
   * room leave (when configured).
   */
  subscribe(key: K, callback: (data: Data) => void): () => void {
    let set = this.callbacks.get(key)
    const wasEmpty = !set || set.size === 0
    if (!set) {
      set = new Set()
      this.callbacks.set(key, set)
    }
    // Each acquisition owns a distinct lease, even when callbacks are shared.
    const deliver = (data: Data) => callback(data)
    set.add(deliver)

    if (this.options.joinOnEverySubscribe || wasEmpty) {
      this.options.join(key)
    }

    return () => {
      const current = this.callbacks.get(key)
      if (current !== set || !current.delete(deliver)) return
      if (current.size === 0) {
        this.callbacks.delete(key)
        this.options.leave?.(key)
      }
    }
  }

  hasSubscribers(key: K): boolean {
    return (this.callbacks.get(key)?.size ?? 0) > 0
  }

  /** Fan a payload out to every callback registered under a key. */
  dispatch(key: K, data: Data): void {
    this.callbacks.get(key)?.forEach((callback) => callback(data))
  }

  /** Re-emit the join for every key with live subscribers (post-reconnect). */
  replay(): void {
    for (const [key, set] of this.callbacks) {
      if (set.size > 0) {
        this.options.join(key)
      }
    }
  }

  clear(): void {
    this.callbacks.clear()
  }
}
